import React from 'react';
import { useTranslation } from 'react-i18next';
import { Select, Option, FormLabel, Box } from '@mui/joy';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { red, orange, blue, grey, green } from '@mui/material/colors';
import i18n from '../../../i18n';

/** Display labels for payment status (English fallbacks for legacy code) */
export const PAYMENT_STATUS_LABELS = {
  unpaid: 'Unpaid',
  partially_paid: 'Partially Paid',
  paid: 'Paid',
  overpaid: 'Overpaid',
  refunded: 'Refunded',
};

function tPaymentStatus(key) {
  return i18n.t(`paymentStatus.${key}`, {
    ns: 'ui',
    defaultValue: PAYMENT_STATUS_LABELS[key] || key,
  });
}

/**
 * Returns { bgcolor, color } for Chip sx, matching this widget's colors.
 * Use for payment status chips on CustomerHomeScreen, CustomerMyShipmentsScreen, etc.
 */
export function getPaymentStatusChipStyle(status) {
  if (!status) return { bgcolor: grey[100], color: grey[700] };
  const key = String(status).toLowerCase().replace(/\s/g, '_');
  const styles = {
    unpaid: { bgcolor: red[50], color: red[700] },
    partially_paid: { bgcolor: orange[50], color: orange[700] },
    paid: { bgcolor: green[50], color: green[800] },
    overpaid: { bgcolor: blue[50], color: blue[700] },
    refunded: { bgcolor: grey[100], color: grey[700] },
  };
  return styles[key] || { bgcolor: grey[100], color: grey[700] };
}

/** Returns { title, color } for payment status icon + Tooltip (color is MUI color key or hex). */
export function getPaymentStatusIconConfig(status) {
  if (!status) return { title: tPaymentStatus('unpaid'), color: red[500] };
  const key = String(status).toLowerCase().replace(/\s/g, '_');
  const configs = {
    unpaid: { title: tPaymentStatus('unpaid'), color: red[500] },
    partially_paid: { title: tPaymentStatus('partially_paid'), color: orange[500] },
    paid: { title: tPaymentStatus('paid'), color: 'success' },
    overpaid: { title: tPaymentStatus('overpaid'), color: blue[500] },
    refunded: { title: tPaymentStatus('refunded'), color: grey[500] },
  };
  return configs[key] || configs.unpaid;
}

export function formatPaymentStatus(status) {
  if (!status) return '—';
  const key = String(status).toLowerCase().replace(/\s/g, '_');
  return tPaymentStatus(key) || status;
}

export default function PaymentStatusOptionList({
    label,
    required,
    paymentStatus,
    setPaymentStatus,
    showAll = false,
    disabled = false,
}) {
    const { t } = useTranslation('ui');
    const resolvedLabel = label ?? t('widgets.paymentStatus');
    // Helper function to get icon and color for each status
    const getStatusIcon = (status) => {
        const statusConfig = {
            'unpaid': {
                color: red[500]
            },
            'partially_paid': {
                color: orange[500]
            },
            'paid': {
                color: 'success'
            },
            'overpaid': {
                color: blue[500]
            },
            'refunded': {
                color: grey[500]
            }
        };

        const config = statusConfig[status];
        if (!config) return null;

        return (
            <MonetizationOnIcon 
                color={config.color === 'success' ? 'success' : undefined}
                sx={config.color !== 'success' ? { color: config.color, fontSize: '18px', mr: 1 } : { fontSize: '18px', mr: 1 }}
            />
        );
    };

    return (
        <Box sx={{ marginBottom: 2 }}>
            <FormLabel
                sx={{
                    fontWeight: 600,
                    color: '#555',
                    marginBottom: '6px',
                    fontSize: '14px',
                }}
            >
                {required ? `${resolvedLabel} *` : resolvedLabel}
            </FormLabel>
            <Select
                value={paymentStatus}
                onChange={(e, newValue) => setPaymentStatus(newValue)}
                required={required}
                disabled={disabled}
                placeholder={t('widgets.selectPaymentStatus')}
                renderValue={(option) => {
                    if (!paymentStatus) return null;
                    return (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {getStatusIcon(paymentStatus)}
                            <span>{tPaymentStatus(paymentStatus) || paymentStatus}</span>
                        </Box>
                    );
                }}
                sx={{
                    backgroundColor: '#f7f9fc',
                    borderRadius: '8px',
                    padding: '8px',
                    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                    '&:hover': {
                        borderColor: '#5f09c2',
                    },
                    '&:focus': {
                        borderColor: '#5f09c2',
                        outline: 'none',
                        boxShadow: '0 0 6px rgba(0, 123, 255, 0.5)',
                    },
                    '&[disabled]': {
                        backgroundColor: '#e9ecef',
                        color: '#6c757d',
                        cursor: 'not-allowed',
                    },
                }}
            >
                {showAll && <Option value="">{t('actions.all')}</Option>}
                <Option value="unpaid">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getStatusIcon('unpaid')}
                        <span>{tPaymentStatus('unpaid')}</span>
                    </Box>
                </Option>
                <Option value="partially_paid">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getStatusIcon('partially_paid')}
                        <span>{tPaymentStatus('partially_paid')}</span>
                    </Box>
                </Option>
                <Option value="paid">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getStatusIcon('paid')}
                        <span>{tPaymentStatus('paid')}</span>
                    </Box>
                </Option>
                <Option value="overpaid">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getStatusIcon('overpaid')}
                        <span>{tPaymentStatus('overpaid')}</span>
                    </Box>
                </Option>
                <Option value="refunded">
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {getStatusIcon('refunded')}
                        <span>{tPaymentStatus('refunded')}</span>
                    </Box>
                </Option>
            </Select>
        </Box>
    );
}

